import React, { useEffect, useState, useRef, useMemo } from "react";
import DataTable from "react-data-table-component";
import {
    AiOutlineCloudUpload,
    AiOutlineDelete,
    AiOutlineDownload,
    AiOutlineEdit,
    AiOutlineClose,
    AiOutlineUpload
} from "react-icons/ai";

// These will be populated from API
let CATEGORIES = [];
let YEARS = [];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB

// Allowed file extensions (updated to include all required types)
const ALLOWED_FILE_EXTENSIONS = [
    '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.txt',
    '.png', '.jpeg', '.jpg', '.svg', '.heic', '.heif'
];

// Allowed MIME types
const ALLOWED_MIME_TYPES = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/plain',
    'image/png',
    'image/jpeg',
    'image/jpg',
    'image/svg+xml',
    'image/heic',
    'image/heif'
];

function validateFile(file) {
    const errors = [];
    
    // Check file size
    if (file.size > MAX_FILE_SIZE) {
        errors.push(`File size must be less than 5MB. Current size: ${(file.size / (1024 * 1024)).toFixed(2)}MB`);
    }
    
    // Check file extension
    const fileName = file.name.toLowerCase();
    const fileExtension = ALLOWED_FILE_EXTENSIONS.find(ext => fileName.endsWith(ext));
    
    if (!fileExtension) {
        errors.push(`Invalid file type. Allowed types: PDF, DOC, DOCX, Excel (XLS, XLSX), Images (PNG, JPEG, SVG, HEIC), TXT`);
    }
    
    // Check MIME type (optional - extension is primary check)
    if (file.type && !ALLOWED_MIME_TYPES.includes(file.type.toLowerCase())) {
        // For images, allow if extension is valid even if MIME type is different
        if (fileExtension && ['.png', '.jpeg', '.jpg', '.svg', '.heic', '.heif'].includes(fileExtension)) {
            if (!file.type.startsWith('image/')) {
                // Still allow if extension is valid (browser might send wrong MIME)
            }
        } else if (fileExtension) {
            // For documents, be more lenient if extension is valid
            // Don't add error if extension is valid
        }
    }
    
    return {
        isValid: errors.length === 0,
        errors: errors
    };
}

// API Configuration
import { API_CONFIG } from '../config/env';
const API_BASE_URL = API_CONFIG.BASE_URL;

// API Functions
async function apiGetCategories() {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_BASE_URL}/documents/categories/`, {
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        }
    });
    
    if (!response.ok) {
        throw new Error('Failed to fetch categories');
    }
    
    const data = await response.json();
    return data.results || data || [];
}

async function apiGetYears() {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_BASE_URL}/documents/years/`, {
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        }
    });
    
    if (!response.ok) {
        throw new Error('Failed to fetch years');
    }
    
    const data = await response.json();
    return data.results || data || [];
}

async function apiGetUserDocuments() {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_BASE_URL}/documents/user-uploads/`, {
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        }
    });
    
    if (!response.ok) {
        throw new Error('Failed to fetch user documents');
    }
    
    const data = await response.json();
    return data.results || data || [];
}

async function apiUploadDocument(formData) {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_BASE_URL}/documents/upload/`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
        },
        body: formData
    });
    
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Upload failed' }));
        console.error('Upload error details:', errorData);
        
        // Handle rate limiting specifically
        if (response.status === 429) {
            const errorMessage = errorData.detail || errorData.error || 'Too many upload requests';
            const retryAfter = response.headers.get('Retry-After');
            let message = 'Upload rate limit exceeded. Please wait a moment before uploading again.';
            if (retryAfter) {
                const seconds = parseInt(retryAfter);
                const minutes = Math.ceil(seconds / 60);
                message = `Upload rate limit exceeded. Please wait ${minutes} minute${minutes > 1 ? 's' : ''} before uploading again.`;
            }
            const error = new Error(message);
            error.isRateLimit = true;
            error.retryAfter = retryAfter;
            throw error;
        }
        
        throw new Error(errorData.error || errorData.detail || 'Upload failed');
    }
    
    const data = await response.json();
    return data;
}

async function apiUpdateDocument(documentId, updateData) {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_BASE_URL}/documents/user-documents/${documentId}/`, {
        method: 'PATCH', // Use PATCH for partial updates
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData)
    });
    
    if (!response.ok) {
        const errorData = await response.json();
        console.error('Update error details:', errorData);
        throw new Error(errorData.error || errorData.detail || 'Update failed');
    }
    
    const data = await response.json();
    return data;
}

async function apiDeleteDocument(documentId) {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_BASE_URL}/documents/user-documents/${documentId}/`, {
        method: 'DELETE',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        }
    });
    
    if (!response.ok) {
        throw new Error('Failed to delete document');
    }
}

async function apiDownloadDocument(documentId) {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_BASE_URL}/documents/user-documents/${documentId}/download/`, {
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${token}`,
        }
    });
    
    if (!response.ok) {
        throw new Error('Failed to download document');
    }
    
    return response;
}

// Removed - using ALLOWED_FILE_EXTENSIONS and ALLOWED_MIME_TYPES defined above

export default function UploadDocuments() {
    // Form inputs state
    const [category, setCategory] = useState("");
    const [year, setYear] = useState("");
    const [file, setFile] = useState(null);
    const [fileError, setFileError] = useState("");
    const [showValidationErrors, setShowValidationErrors] = useState(false);
    const [uploadError, setUploadError] = useState("");
    const [uploadSuccess, setUploadSuccess] = useState("");

    // Document data and loading
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    
    // Categories and years from API
    const [categories, setCategories] = useState([]);
    const [years, setYears] = useState([]);
    const [categoriesLoading, setCategoriesLoading] = useState(true);

    // Search term
    const [searchTerm, setSearchTerm] = useState("");

    // Pagination & sorting
    const [perPage, setPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [sortColumn, setSortColumn] = useState(null);
    const [sortDirection, setSortDirection] = useState("asc");

    // Modals and edit form data
    const [editingDoc, setEditingDoc] = useState(null);
    const [editCategory, setEditCategory] = useState("");
    const [editYear, setEditYear] = useState("");
    const [editFile, setEditFile] = useState(null);
    const [editFileError, setEditFileError] = useState("");
    const editFileInput = useRef();
    const [completedYears, setCompletedYears] = useState(new Set());

    const [deleteDoc, setDeleteDoc] = useState(null);

    // Refs for inputs
    const fileInput = useRef();

    // Fetch categories and years on mount
    useEffect(() => {
        async function fetchCategoriesAndYears() {
            setCategoriesLoading(true);
            try {
                const [categoriesData, yearsData] = await Promise.all([
                    apiGetCategories(),
                    apiGetYears()
                ]);
                setCategories(categoriesData);
                setYears(yearsData);
            } catch (error) {
                console.error('Failed to fetch categories/years:', error);
                setCategories([]);
                setYears([]);
            }
            setCategoriesLoading(false);
        }
        fetchCategoriesAndYears();
    }, []);

    // Fetch user documents on mount
    useEffect(() => {
        async function fetchDocs() {
            setLoading(true);
            try {
                const documents = await apiGetUserDocuments();
                setData(documents);
                
                // Check for completed tax filings - only years with final returns that user has received
                const completedYearsSet = new Set();
                try {
                    // Get all final tax returns for the user (same as downloadTaxReturnFinalCopy.jsx)
                    const token = localStorage.getItem('authToken');
                    const response = await fetch(`${API_BASE_URL}/user/tax-returns/final/`, {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        }
                    });
                    
                    if (response.ok) {
                        const data = await response.json();
                        const finalReturns = data.data || [];
                        
                        // Only mark years as completed if user has actually received the final copy
                        // IMPORTANT: Only consider regular uploads (category is NULL or 'regular')
                        // Do NOT include amendment/notices uploads (category is 'amendment' or 'notices')
                        console.log('Final returns data:', finalReturns);
                        finalReturns.forEach(returnData => {
                            // Filter: Only process regular uploads, not amendment/notices uploads
                            const category = returnData.category;
                            const isRegularUpload = !category || category === 'regular' || category === null;
                            
                            if (isRegularUpload) {
                                console.log(`Year ${returnData.year} (regular): user_received_at = ${returnData.user_received_at}`);
                                if (returnData.user_received_at) {
                                    completedYearsSet.add(returnData.year);
                                    console.log(`Marking year ${returnData.year} as completed (regular upload)`);
                                }
                            } else {
                                console.log(`Skipping year ${returnData.year} - category is '${category}' (amendment/notices, not regular upload)`);
                            }
                        });
                        console.log('Completed years set:', Array.from(completedYearsSet));
                    }
                } catch (error) {
                    console.error('Failed to check final returns:', error);
                }
                setCompletedYears(completedYearsSet);
            } catch (error) {
                console.error('Failed to fetch documents:', error);
                setData([]);
            }
            setLoading(false);
        }
        fetchDocs();
    }, [years]);

    // Filtered & searched data before pagination & display
    const filteredData = useMemo(() => {
        try {
            if (!data || !Array.isArray(data)) return [];
            if (!searchTerm) return data;
            const term = searchTerm.toLowerCase();
            return data.filter(
                (doc) => {
                    if (!doc) return false;
                    // Search only by category
                    const categoryMatch = doc.category && typeof doc.category === 'string' && doc.category.toLowerCase().includes(term);
                    return categoryMatch;
                }
            );
        } catch (error) {
            console.error('Error filtering documents:', error);
            return data || [];
        }
    }, [data, searchTerm]);

    // Sort data on client side based on sortColumn and direction
    const sortedData = useMemo(() => {
        if (!sortColumn) return filteredData;
        const sortFn = (a, b) => {
            let aVal =
                typeof sortColumn === "function" ? sortColumn(a) : a[sortColumn];
            let bVal =
                typeof sortColumn === "function" ? sortColumn(b) : b[sortColumn];
            aVal = aVal === null || aVal === undefined ? "" : aVal;
            bVal = bVal === null || bVal === undefined ? "" : bVal;
            if (typeof aVal === "string") aVal = aVal.toLowerCase();
            if (typeof bVal === "string") bVal = bVal.toLowerCase();

            if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
            if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
            return 0;
        };

        return [...filteredData].sort(sortFn);
    }, [filteredData, sortColumn, sortDirection]);

    // Paginated rows for current page
    const paginatedRows = useMemo(() => {
        const start = (currentPage - 1) * perPage;
        return sortedData.slice(start, start + perPage);
    }, [sortedData, currentPage, perPage]);

    // Columns for react-data-table-component
    const columns = useMemo(() => [
        {
            name: "S.No",
            width: "80px",
            cell: (_row, idx) => (currentPage - 1) * perPage + idx + 1,
            ignoreRowClick: true,
            sortable: false,
            style: { fontWeight: 600, color: "#4338ca" },
        },
        {
            name: "Category",
            selector: (row) => row.category,
            sortable: true,
            style: { fontWeight: 500 },
            wrap: true,
        },
        {
            name: "Year",
            selector: (row) => row.year,
            sortable: true,
            width: "90px",
            style: { justifyContent: "center", display: "flex" },
        },
        {
            name: "Filename",
            selector: (row) => row.filename,
            sortable: true,
            cell: (row) => (
                <button
                    onClick={() => handleDownload(row)}
                    style={{
                        color: "#256bb7",
                        textDecoration: "underline",
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        fontWeight: 600,
                    }}
                    title={`Download ${row.filename}`}
                    disabled={loading}
                >
                    <AiOutlineDownload style={{ verticalAlign: "middle", marginRight: 4 }} />
                    {row.filename}
                </button>
            ),
            wrap: true,
        },
        {
            name: "Datetime",
            selector: (row) => row.datetime,
            sortable: true,
            width: "180px",
            style: { color: "#6b7280", fontSize: 13 },
            cell: (row) => {
                if (!row.datetime) return 'N/A';
                try {
                    // Convert to CST timezone and format as 24-hour
                    const date = new Date(row.datetime);
                    const cstDate = new Date(date.toLocaleString("en-US", {timeZone: "America/Chicago"}));
                    return cstDate.toLocaleString('en-US', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                        hour12: false,
                        timeZone: 'America/Chicago'
                    }) + ' CST';
                } catch (error) {
                    return row.datetime;
                }
            },
            wrap: true,
        },
        {
            name: "Actions",
            cell: (row) => (
                <div style={{ display: "flex", gap: 8 }}>
                    <button
                        onClick={() => openEdit(row)}
                        aria-label={`Edit document ${row.filename}`}
                        style={buttonStyle("#4338ca")}
                    >
                        <AiOutlineEdit /> Edit
                    </button>
                    <button
                        onClick={() => openDelete(row)}
                        aria-label={`Delete document ${row.filename}`}
                        style={buttonStyle("#dc2626")}
                    >
                        <AiOutlineDelete /> Delete
                    </button>
                </div>
            ),
            ignoreRowClick: true,
        },
    ], [currentPage, perPage, loading]);

    // Action button styles
    function buttonStyle(bgColor) {
        return {
            background: bgColor,
            color: "white",
            borderRadius: 6,
            border: "none",
            cursor: "pointer",
            padding: "6px 12px",
            fontSize: 13,
            display: "flex",
            alignItems: "center",
            gap: 4,
            userSelect: "none",
            fontWeight: 600,
        };
    }

    // Validation function
    function isFormValid() {
        return category && year && file && !fileError;
    }

    // Check if form has validation errors (only show after user tries to submit)
    function hasValidationErrors() {
        return showValidationErrors && (!category || !year || !file || !!fileError);
    }

    // File input change handler for upload form
    function onFileChange(e) {
        const selected = e.target.files[0];
        if (!selected) {
            setFile(null);
            setFileError("");
            return;
        }
        
        // Validate file using the validateFile function
        const validation = validateFile(selected);
        if (!validation.isValid) {
            setFile(null);
            setFileError(validation.errors.join('. '));
            if (fileInput.current) fileInput.current.value = "";
            return;
        }
        
        // File is valid
        setFile(selected);
        setFileError("");
    }

    // Upload Handler
    async function handleUpload(e) {
        e.preventDefault();
        setShowValidationErrors(true);
        if (!isFormValid()) return;

        // Check if tax filing is already completed for this year
        // Find the year object to get the actual year number (not the UUID)
        const selectedYearObj = years.find(y => String(y.id) === String(year));
        if (selectedYearObj && completedYears.has(selectedYearObj.year)) {
            alert(`Tax for ${selectedYearObj.year} has already been filed. You cannot upload another document for this year.`);
            return;
        }

        setLoading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            // Send category and year as UUID strings, not integers
            formData.append('category', String(category || ''));
            formData.append('year', String(year || ''));
            formData.append('title', file.name);

            const result = await apiUploadDocument(formData);
            
            // Refresh the documents list
            const documents = await apiGetUserDocuments();
            setData(documents);
            
            // Reset form
            setCategory("");
            setYear("");
            setFile(null);
            setFileError("");
            setShowValidationErrors(false);
            setUploadError("");
            setUploadSuccess("Document uploaded successfully!");
            if (fileInput.current) fileInput.current.value = "";
            
            // Clear success message after 5 seconds
            setTimeout(() => setUploadSuccess(""), 5000);
            
        } catch (error) {
            console.error('Upload failed:', error);
            
            // Set user-friendly error message
            if (error.isRateLimit) {
                setUploadError(error.message || "Upload rate limit exceeded. Please wait a moment before trying again.");
            } else {
                setUploadError(error.message || "Upload failed. Please try again.");
            }
            
            // Clear error message after 10 seconds
            setTimeout(() => setUploadError(""), 10000);
        }
        setLoading(false);
    }

    // Download file handler
    async function handleDownload(row) {
        setLoading(true);
        try {
            const response = await apiDownloadDocument(row.id);
            
            // Get the filename from the response headers or use the row filename
            const contentDisposition = response.headers.get('Content-Disposition');
            let filename = row.filename;
            
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="(.+)"/);
                if (filenameMatch) {
                    filename = filenameMatch[1];
                }
            }
            
            // Create a blob from the response
            const blob = await response.blob();
            
            // Create a download link and trigger download
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            link.style.display = 'none';
            document.body.appendChild(link);
            link.click();
            
            // Clean up
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            
        } catch (error) {
            console.error('Download failed:', error);
            alert('Failed to download file. Please try again.');
        }
        setLoading(false);
    }

    // Pagination handlers
    function handlePageChange(page) {
        setCurrentPage(page);
    }
    function handlePerRowsChange(newPerPage, page) {
        setPerPage(newPerPage);
        setCurrentPage(page);
    }

    // Edit modal handlers
    function openEdit(row) {
        setEditingDoc(row);
        setEditCategory(row.category_id); // Use category_id instead of category
        setEditYear(row.year_id); // Use year_id instead of year
        setEditFile(null); // New file selection (optional)
        setEditFileError("");
        if (editFileInput.current) editFileInput.current.value = "";
    }
    function closeEdit() {
        setEditingDoc(null);
        setEditFile(null);
        setEditFileError("");
    }
    function onEditFileChange(e) {
        const selected = e.target.files[0];
        if (!selected) {
            setEditFile(null);
            setEditFileError("");
            return;
        }
        
        // Validate file using the validateFile function
        const validation = validateFile(selected);
        if (!validation.isValid) {
            setEditFile(null);
            setEditFileError(validation.errors.join('. '));
            if (editFileInput.current) editFileInput.current.value = "";
            return;
        }
        
        // File is valid
        setEditFile(selected);
        setEditFileError("");
    }
    // Save edited document
    async function saveEdit() {
        setLoading(true);
        try {
            const datetime = new Date().toISOString().replace("T", " ").substring(0, 16);
            
            // Prepare JSON data for update (metadata only)
            // Send category and year as UUID strings, not integers
            const updateData = {
                category: String(editCategory || ''),
                year: String(editYear || ''),
                title: editFile ? editFile.name : editingDoc.filename,
                description: editingDoc.description || '' // Include description if available
            };
            
            // Update the document metadata via API
            await apiUpdateDocument(editingDoc.id, updateData);
            
            // If a new file was selected, create a new document with the file
            if (editFile) {
                const formData = new FormData();
                formData.append('file', editFile);
                // Send category and year as UUID strings, not integers
                formData.append('category', String(editCategory || ''));
                formData.append('year', String(editYear || ''));
                formData.append('title', editFile.name);
                
                // Create new document with file
                await apiUploadDocument(formData);
                
                // Delete the old document
                await apiDeleteDocument(editingDoc.id);
            }
            
            // Refresh the documents list
            const documents = await apiGetUserDocuments();
            setData(documents);
            
            setLoading(false);
            closeEdit();
            // Changes saved successfully
        } catch (error) {
            console.error('Save failed:', error);
            // Save failed
            setLoading(false);
        }
    }

    // Delete modal handlers
    function openDelete(row) {
        setDeleteDoc(row);
    }
    function closeDelete() {
        setDeleteDoc(null);
    }
    async function confirmDelete() {
        setLoading(true);
        try {
            await apiDeleteDocument(deleteDoc.id);
            
            // Refresh the documents list
            const documents = await apiGetUserDocuments();
            setData(documents);
            
            closeDelete();
            // Document deleted successfully
        } catch (error) {
            console.error('Delete failed:', error);
            // Delete failed
        }
        setLoading(false);
    }

    // Styles for DataTable
   const customStyles = {
    rows: { style: { fontSize: 15, background: "#f8fafc" } },
    headRow: { style: { fontWeight: 700, fontSize: 15, background: "#eceffd", color: "#4338ca", minHeight: "46px" } },
    headCells: { style: { borderBottom: "1.5px solid #a5b4fc", paddingLeft: "18px", paddingRight: "18px" } },
    cells: { style: { paddingLeft: "18px", paddingRight: "18px" } },
    pagination: { style: { borderTop: "solid 1px #eceffd", color: "#4338ca", fontWeight: 600 } }
  };

    // Sort handler for DataTable
    function onSort(column, sortDirection) {
        if (column.selector) {
            setSortColumn(column.selector);
            setSortDirection(sortDirection);
        }
    }

    // Modal Component
    function Modal({ onClose, title, children }) {
        return (
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="modal-title"
                style={{
                    position: "fixed",
                    top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: "rgba(0,0,0,0.25)",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    zIndex: 10000,
                }}
                onClick={onClose}
            >
                <div
                    onClick={(e) => e.stopPropagation()}
                    style={{
                        backgroundColor: "white",
                        borderRadius: 12,
                        padding: 28,
                        maxWidth: 480,
                        width: "100%",
                        position: "relative",
                        userSelect: "none",
                        boxShadow: "0 5px 20px rgba(99, 102, 241, 0.3)",
                    }}
                >
                    <h3
                        id="modal-title"
                        style={{
                            fontWeight: 700,
                            marginBottom: 18,
                            fontSize: 21,
                            color: "#2d3393",
                        }}
                    >
                        {title}
                    </h3>
                    {children}
                    <button
                        aria-label="Close"
                        onClick={onClose}
                        style={{
                            position: "absolute",
                            top: 12,
                            right: 12,
                            background: "transparent",
                            border: "none",
                            fontSize: 24,
                            color: "#555",
                            cursor: "pointer",
                            userSelect: "none",
                        }}
                    >
                        <AiOutlineClose />
                    </button>
                </div>
            </div>
        );
    }

    return (
        <section style={{ userSelect: "none" }}>
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 20
                }}
            >
                {/* Left: Icon and Heading */}
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <AiOutlineUpload
                        style={{
                            fontSize: 38,
                            color: "#4338ca",
                            filter: "drop-shadow(0px 1px 4px #b5beff77)",
                            verticalAlign: "middle"
                        }}
                    />
                    <span
                        style={{
                            fontSize: 38,
                            marginRight: 10,
                            color: "#4338ca",
                            verticalAlign: "middle",
                            filter: "drop-shadow(0px 1px 4px #b5beff77)",
                        }}
                    >
                        Uploaded Documents
                    </span>
                </div>

                {/* Right: Search bar */}
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <input
                        type="text"
                        placeholder="Search by category..."
                        aria-label="Search by category"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        style={{
                            padding: "8px 14px",
                            fontSize: 15,
                            borderRadius: 6,
                            border: "1.5px solid #a5b4fc",
                            maxWidth: 260,
                            background: "#f9f9fb",
                            userSelect: "text"
                        }}
                    />
                    {searchTerm && (
                        <button
                            type="button"
                            onClick={() => setSearchTerm("")}
                            style={{
                                background: "none",
                                border: "none",
                                cursor: "pointer",
                                padding: 4,
                                display: "flex",
                                alignItems: "center",
                                color: "#666"
                            }}
                            title="Clear search"
                        >
                            <AiOutlineClose size={16} />
                        </button>
                    )}
                </div>
            </div>


            <form
                onSubmit={handleUpload}
                style={{
                    display: "flex",
                    gap: 12,
                    marginBottom: 28,
                    flexWrap: "wrap",
                    alignItems: "center",
                    maxWidth: 850,
                }}
            >
                <div style={{ display: "flex", flexDirection: "column", minWidth: 150 }}>
                    <select
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                        required
                        style={{
                            padding: 8,
                            borderRadius: 6,
                            border: `1px solid ${(!category && showValidationErrors) ? "#dc2626" : "#ced4da"}`,
                            minWidth: 150,
                        }}
                    >
                        <option value="">Select Category</option>
                        {categories.map((cat) => (
                            <option key={cat.id} value={cat.id}>
                                {cat.name}
                            </option>
                        ))}
                    </select>
                    {(!category && showValidationErrors) && (
                        <span style={{ color: "#dc2626", fontSize: "12px", marginTop: "2px" }}>
                            Category is required
                        </span>
                    )}
                </div>

                <div style={{ display: "flex", flexDirection: "column", minWidth: 120 }}>
                    <select
                        value={year}
                        onChange={(e) => setYear(e.target.value)}
                        required
                        style={{
                            padding: 8,
                            borderRadius: 6,
                            border: `1px solid ${(!year && showValidationErrors) ? "#dc2626" : "#ced4da"}`,
                            minWidth: 120,
                        }}
                    >
                        <option value="">Select Year</option>
                        {years.map((y) => (
                            <option key={y.id} value={y.id} disabled={completedYears.has(y.year)}>
                                {y.year} {completedYears.has(y.year) ? '(Already Filed)' : ''}
                            </option>
                        ))}
                    </select>
                    {(!year && showValidationErrors) && (
                        <span style={{ color: "#dc2626", fontSize: "12px", marginTop: "2px" }}>
                            Year is required
                        </span>
                    )}
                </div>

                <div style={{ display: "flex", flexDirection: "column", flexGrow: 1, minWidth: 180 }}>
                    <input
                        type="file"
                        onChange={onFileChange}
                        ref={fileInput}
                        accept=".pdf,.doc,.docx,.txt"
                        style={{
                            flexGrow: 1,
                            minWidth: 180,
                            padding: 9,
                            borderRadius: 6,
                            border: `1px solid ${(!file && showValidationErrors) ? "#dc2626" : "#ced4da"}`,
                            fontSize: 14,
                            cursor: "pointer",
                        }}
                    />
                    {(!file && showValidationErrors) && (
                        <span style={{ color: "#dc2626", fontSize: "12px", marginTop: "2px" }}>
                            File is required
                        </span>
                    )}
                </div>

                <button
                    type="submit"
                    disabled={!isFormValid()}
                    style={{
                        padding: "7px 20px",
                        backgroundColor: isFormValid() ? "#4338ca" : "#a9b8f2",
                        borderRadius: 6,
                        border: "none",
                        color: "white",
                        fontWeight: 600,
                        cursor: isFormValid() ? "pointer" : "not-allowed",
                        userSelect: "none",
                        opacity: isFormValid() ? 1 : 0.6,
                        transition: "all 0.2s ease",
                    }}
                    title={
                        !category ? "Please select a category" :
                        !year ? "Please select a year" :
                        !file ? "Please select a file" :
                        fileError ? fileError :
                        "Upload document"
                    }
                >
                    <AiOutlineCloudUpload size={18} style={{ marginRight: 6 }} />
                    Upload
                </button>

                {fileError && (
                    <div style={{ color: "#dc2626", userSelect: "text", marginTop: 4 }}>
                        {fileError}
                    </div>
                )}
            </form>

            {/* Upload Error Message */}
            {uploadError && (
                <div
                    style={{
                        padding: "12px 16px",
                        marginBottom: 20,
                        backgroundColor: "#fee2e2",
                        border: "1px solid #fca5a5",
                        borderRadius: 6,
                        color: "#991b1b",
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        maxWidth: 850,
                    }}
                >
                    <AiOutlineClose
                        size={18}
                        style={{ cursor: "pointer", flexShrink: 0 }}
                        onClick={() => setUploadError("")}
                    />
                    <span style={{ flex: 1, userSelect: "text" }}>{uploadError}</span>
                </div>
            )}

            {/* Upload Success Message */}
            {uploadSuccess && (
                <div
                    style={{
                        padding: "12px 16px",
                        marginBottom: 20,
                        backgroundColor: "#d1fae5",
                        border: "1px solid #86efac",
                        borderRadius: 6,
                        color: "#065f46",
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        maxWidth: 850,
                    }}
                >
                    <AiOutlineCloudUpload
                        size={18}
                        style={{ flexShrink: 0 }}
                    />
                    <span style={{ flex: 1, userSelect: "text" }}>{uploadSuccess}</span>
                </div>
            )}

            {/* DataTable */}
            <DataTable
                columns={columns}
                data={paginatedRows}
                highlightOnHover
                striped
                pagination
                paginationServer={false}
                paginationTotalRows={sortedData.length}
                paginationDefaultPage={currentPage}
                paginationPerPage={perPage}
                paginationRowsPerPageOptions={[10, 20, 30, 40, 50]}
                onChangeRowsPerPage={handlePerRowsChange}
                onChangePage={handlePageChange}
                sortServer={true}
                onSort={onSort}
                noHeader
                responsive
                persistTableHead
                customStyles={customStyles}
                progressPending={loading}
            />

            {/* Edit Modal */}
            {editingDoc && (
                <Modal onClose={closeEdit} title="Edit Document">
                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            saveEdit();
                        }}
                        style={{ display: "flex", flexDirection: "column", gap: 18 }}
                    >
                        <div>
                            <strong>Category: </strong>
                            {editCategory}
                        </div>
                        <div>
                            <strong>Year: </strong>
                            {editYear}
                        </div>
                        <div>
                            <div style={{ marginBottom: 10 }}>
                                <strong>Current file: </strong>
                                <span style={{ color: "#256bb7" }}>{editingDoc.filename}</span>
                            </div>
                            <label
                                htmlFor="edit-file"
                                style={{ fontWeight: 600, marginBottom: 6, display: "block" }}
                            >
                                Select New File (Optional)
                            </label>
                            <input
                                id="edit-file"
                                type="file"
                                ref={editFileInput}
                                accept=".pdf,.doc,.docx,.txt"
                                onChange={onEditFileChange}
                                aria-label="Select new file"
                                style={{
                                    padding: "8px",
                                    borderRadius: "6px",
                                    border: "1px solid #ced4da",
                                    fontSize: "14px",
                                    cursor: "pointer",
                                    width: "100%",
                                    maxWidth: "300px"
                                }}
                            />
                            {editFile && (
                                <div style={{ marginTop: 5, color: "#256bb7" }}>
                                    New file selected: {editFile.name}
                                </div>
                            )}
                            {!!editFileError && (
                                <div style={{ color: "#dc2626" }}>{editFileError}</div>
                            )}
                        </div>
                        <div style={{ display: "flex", justifyContent: "flex-end", gap: 15 }}>
                            <button 
                                type="button" 
                                onClick={closeEdit} 
                                style={{
                                    ...buttonStyle("#6c757d"),
                                    cursor: "pointer",
                                    opacity: 1
                                }}
                            >
                                Cancel
                            </button>
                            <button 
                                type="submit" 
                                disabled={loading}
                                style={{
                                    ...buttonStyle("#4338ca"),
                                    cursor: loading ? "not-allowed" : "pointer",
                                    opacity: loading ? 0.6 : 1
                                }}
                            >
                                {loading ? "Saving..." : "Save"}
                            </button>
                        </div>
                    </form>
                </Modal>
            )}

            {/* Delete Modal */}
            {deleteDoc && (
                <Modal onClose={closeDelete} title="Delete Document">
                    <div style={{ marginBottom: 20 }}>
                        Are you sure you want to delete <strong>{deleteDoc.filename}</strong>?
                    </div>
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: 15 }}>
                        <button onClick={closeDelete} style={buttonStyle("#a9accb")}>
                            Cancel
                        </button>
                        <button onClick={confirmDelete} style={buttonStyle("#dc2626")}>
                            Delete
                        </button>
                    </div>
                </Modal>
            )}
        </section>
    );
}
